import { useState, useEffect, useRef } from 'react';
import { Zap, Check, RefreshCw, TrendingUp, Package, DollarSign, AlertCircle, LogOut, Settings, X, Wifi, WifiOff, BarChart3, Activity, ShoppingCart, Clock, Search, CheckSquare, Square } from 'lucide-react';

// API URL - automatically uses production URL when deployed
const API_URL = process.env.REACT_APP_API_URL || '';

const api = {
  async call(endpoint, options = {}) {
    const token = localStorage.getItem('authToken');

    // Debug: Log if token is missing
    if (!token) {
    }

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    console.log(`📡 API Call: ${options.method || 'GET'} ${endpoint}`);

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.json();

      // If auth error, suggest re-login
      if (response.status === 401 || response.status === 403) {
        throw new Error('Session expired. Please log out and log back in.');
      }
      throw new Error(error.error || 'Request failed');
    }
    return response.json();
  }
};

function CostPriceModal({ isOpen, onClose, product, onSave }) {
  const [costPrice, setCostPrice] = useState('0.00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (product) setCostPrice(product.cost_price || '0.00');
  }, [product]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.call(`/api/products/${product.id}/cost-price`, {
        method: 'POST',
        body: JSON.stringify({ cost_price: parseFloat(costPrice) })
      });
      onSave();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !product) return null;

  const currentPrice = parseFloat(product.price);
  const cost = parseFloat(costPrice);
  const margin = cost > 0 ? (((currentPrice - cost) / currentPrice) * 100).toFixed(1) : 0;
  const profit = (currentPrice - cost).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-slate-800/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 max-w-md w-full shadow-glass my-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">Set Cost Price</h2>
            <p className="text-sm text-gray-400 mt-1">{product.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-white font-medium mb-2">Cost Price (What you paid)</label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-400 text-lg">$</span>
              <input
                type="number"
                step="0.01"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                className="w-full pl-8 pr-4 py-3 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-lg text-white text-lg focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
                placeholder="0.00"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">AI will never recommend below this price</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Current Selling Price:</span>
              <span className="text-white font-bold text-lg">${currentPrice.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Your Cost:</span>
              <span className="text-white font-semibold">${cost.toFixed(2)}</span>
            </div>
            <div className="h-px bg-slate-700"></div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Profit per Unit:</span>
              <span className={`font-bold text-lg ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                ${profit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Profit Margin:</span>
              <span className={`font-semibold ${margin >= 20 ? 'text-green-400' : margin >= 10 ? 'text-yellow-400' : 'text-red-400'}`}>
                {margin}%
              </span>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            type="button"
            className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:bg-slate-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-md shadow-emerald-500/20"
          >
            {saving ? 'Saving...' : 'Save Cost Price'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ isOpen, onClose, onConnect, shopifyConnected }) {
  const [formData, setFormData] = useState({ shop: '', accessToken: '' });
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = async () => {
    if (!formData.shop || !formData.accessToken) {
      setError('Please fill in all fields');
      return;
    }

    setConnecting(true);
    setError(null);
    try {
      await api.call('/api/shopify/connect', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      onConnect();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 max-w-2xl w-full shadow-glass max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center space-x-3">
            <Settings className="w-6 h-6 text-emerald-400" />
            <h2 className="text-2xl font-bold text-white">Shopify Connection</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {shopifyConnected ? (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Check className="w-6 h-6 text-green-400" />
                <div>
                  <h3 className="text-green-300 font-semibold">Connected Successfully!</h3>
                  <p className="text-green-200/80 text-sm">Your Shopify store is synced and ready.</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <h3 className="text-blue-300 font-semibold mb-2">How to get your credentials:</h3>
                <ol className="text-sm text-blue-200 space-y-2">
                  <li>1. Go to your Shopify Admin → Settings → Apps and sales channels</li>
                  <li>2. Click "Develop apps" → Create an app</li>
                  <li>3. Configure Admin API scopes (read_products, write_products, read_orders)</li>
                  <li>4. Install the app and copy your access token</li>
                </ol>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-white font-medium mb-2">Shop Domain</label>
                  <input
                    type="text"
                    value={formData.shop}
                    onChange={(e) => setFormData({ ...formData, shop: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
                    placeholder="your-store.myshopify.com"
                  />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Access Token</label>
                  <input
                    type="password"
                    value={formData.accessToken}
                    onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
                    placeholder="shpat_xxxxxxxxxxxxx"
                  />
                </div>
              </div>

              <button
                onClick={handleConnect}
                disabled={connecting}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:bg-slate-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-md shadow-emerald-500/20"
              >
                {connecting ? 'Connecting...' : 'Connect Shopify'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Display actual scheduled time (synced with cron-job.org schedule)
function NextAnalysisTime({ nextAnalysisDue, showIcon = true }) {
  // CRITICAL FIX: If backend doesn't provide time, calculate client-side
  // Cron runs every 30 minutes at :00 and :30 marks
  let displayTime = nextAnalysisDue;

  if (!displayTime) {
    // Calculate next :00 or :30 mark
    const now = new Date();
    const minutes = now.getMinutes();
    const nextCronMinute = minutes < 30 ? 30 : 60;
    const minutesToAdd = nextCronMinute - minutes;

    const nextCron = new Date(now.getTime() + minutesToAdd * 60 * 1000);
    nextCron.setSeconds(0);
    nextCron.setMilliseconds(0);
    displayTime = nextCron.toISOString();
  }

  const formatTime = (dateString) => {
    // CRITICAL: Database stores timestamps without timezone, so append Z to treat as UTC
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    const now = new Date();

    // Format time as "4:30 PM"
    const timeString = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    });

    // Check if it's today or tomorrow
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const scheduleDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((scheduleDate - today) / (1000 * 60 * 60 * 24));

    let dayPrefix = '';
    if (diffDays === 0) {
      dayPrefix = 'Today at ';
    } else if (diffDays === 1) {
      dayPrefix = 'Tomorrow at ';
    } else {
      // Show day of week for future dates
      dayPrefix = date.toLocaleDateString('en-US', { weekday: 'short' }) + ' at ';
    }

    return dayPrefix + timeString + ' EST';
  };

  return (
    <div className="flex items-center space-x-2">
      {showIcon && <Clock className="w-4 h-4 text-emerald-400" />}
      <span className="text-white font-semibold">
        {formatTime(displayTime)}
      </span>
    </div>
  );
}

function ResetCountdownTimer({ timeUntilReset, onRefresh }) {
  const [time, setTime] = useState(timeUntilReset);

  useEffect(() => {
    setTime(timeUntilReset);
  }, [timeUntilReset]);

  // FIX: Separated timer logic from refresh logic to prevent memory leak
  useEffect(() => {
    const interval = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    // Cleanup function runs on component unmount
    return () => clearInterval(interval);
  }, []); // Empty dependency array ensures this only runs once

  // FIX: Separate effect for handling the refresh action when timer hits zero
  useEffect(() => {
    if (time === 0) {
      onRefresh();
    }
  }, [time, onRefresh]);

  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = time % 60;

  return (
    <div className="flex items-center space-x-2">
      <RefreshCw className="w-4 h-4 text-green-400" />
      <span className="text-gray-400 text-sm">
        Resets in: {hours.toString().padStart(2, '0')}:{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
      </span>
    </div>
  );
}

function ProductDashboard({ userEmail, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCostPriceModal, setShowCostPriceModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState({ analyzing: false, timeRemaining: 1800, nextAnalysisDue: null, manualUsed: 0, manualRemaining: 10, timeUntilReset: 0, resetTime: null });
  const [showAnalysisResults, setShowAnalysisResults] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [assignedApp, setAssignedApp] = useState(null);
  const [hasTriedAutoConnect, setHasTriedAutoConnect] = useState(() => {
    // Check localStorage to prevent infinite redirects
    const tried = localStorage.getItem('hasTriedAutoConnect');
    return tried === 'true';
  });
  const [connectionCheckComplete, setConnectionCheckComplete] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showNewRecommendationsAlert, setShowNewRecommendationsAlert] = useState(false);
  const [newRecommendationsCount, setNewRecommendationsCount] = useState(0);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [expandedRecommendation, setExpandedRecommendation] = useState(null);

  // Update notification - PERMANENT banner (always shows)
  const UPDATE_VERSION = 'v2.1-superior'; // Latest version
  const [showUpdateBanner] = useState(true); // Always show, no dismiss

  useEffect(() => {
    initializeDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeDashboard = async () => {
    // Run these in parallel for faster loading
    await Promise.all([
      checkShopifyConnection(),
      checkAssignedApp()
    ]);

    // Mark connection check as complete
    setConnectionCheckComplete(true);

    // Load dashboard data (non-blocking)
    loadDashboardData();
    loadAnalysisStatus();
  };

  const checkShopifyConnection = async () => {
    try {
      console.log('🔍 Checking Shopify connection status...');
      const data = await api.call('/api/shopify/status');
      console.log('📊 Shopify connection result:', data);
      setShopifyConnected(data.connected);

      if (data.connected) {
        console.log(`✅ Shopify connected: ${data.shop}`);
        // Clear the auto-connect flag when successfully connected
        localStorage.removeItem('hasTriedAutoConnect');
      } else {
        console.log('⚠️ Shopify not connected');
      }

      return data.connected;
    } catch (err) {
      console.error('❌ Failed to check Shopify connection:', err);
      return false;
    }
  };

  const checkAssignedApp = async () => {
    try {
      console.log('🔍 Checking for assigned Shopify app...');
      const data = await api.call('/api/user/assigned-app');
      setAssignedApp(data.app);

      if (data.app) {
        console.log('✅ User has assigned app:', data.app.app_name);
      } else {
        console.log('⚠️ No app assigned to user');
      }

      return data.app;
    } catch (err) {
      console.error('❌ Failed to check assigned app:', err);
      return null;
    }
  };

  // Auto-redirect to OAuth ONLY ONCE if user has assigned app but no Shopify connection
  useEffect(() => {
    // Only attempt redirect after connection check is complete
    if (!connectionCheckComplete) {
      return;
    }

    // Check if we should auto-redirect
    const shouldAutoRedirect = !hasTriedAutoConnect && assignedApp && !shopifyConnected;

    if (shouldAutoRedirect) {
      console.log('🚀 Auto-redirecting to OAuth install for assigned app:', assignedApp);
      console.log(`   App: ${assignedApp.app_name}`);
      console.log(`   Shop: ${assignedApp.shop_domain}`);

      setHasTriedAutoConnect(true);
      // Persist to localStorage to prevent infinite redirects
      localStorage.setItem('hasTriedAutoConnect', 'true');

      // Small delay to ensure state is saved before redirect
      setTimeout(() => {
        const installUrl = `${API_URL}/api/shopify/install?shop=${assignedApp.shop_domain}&app_id=${assignedApp.id}&user_email=${encodeURIComponent(userEmail)}`;
        console.log(`   Redirecting to: ${installUrl}`);
        window.location.href = installUrl;
      }, 500);
    } else if (connectionCheckComplete) {
      console.log('ℹ️ Skipping auto-redirect:', {
        connectionCheckComplete,
        hasTriedAutoConnect,
        hasAssignedApp: !!assignedApp,
        shopifyConnected
      });
    }
  }, [connectionCheckComplete, assignedApp, shopifyConnected, hasTriedAutoConnect, userEmail]);

  // Load initial timer status (no auto-refresh - timer counts down locally)
  useEffect(() => {
    if (shopifyConnected) {
      loadAnalysisStatus();
    }
  }, [shopifyConnected]);

  // Auto-select products when products change (for ≤10 products)
  useEffect(() => {
    if (products.length > 0 && products.length <= 10) {
      // Auto-select all products with cost price set
      const productsWithCost = products.filter(p => p.cost_price > 0).map(p => p.id);
      setSelectedProductIds(productsWithCost);
      console.log(`✅ Auto-selected ${productsWithCost.length} products (≤10 total products)`);
    }
  }, [products]);

  const loadAnalysisStatus = async () => {
    try {
      console.log('🔍 Loading analysis status from API...');
      // Add cache-busting query parameter to prevent stale data
      const cacheBuster = Date.now();
      const data = await api.call(`/api/analysis/status?_=${cacheBuster}`);
      console.log('📊 API Response:', data);
      console.log('   manualUsedToday:', data.manualUsedToday);
      console.log('   manualRemaining:', data.manualRemaining);
      console.log('   timeRemaining:', data.timeRemaining);
      console.log('   nextAnalysisDue:', data.nextAnalysisDue);

      const newStatus = {
        analyzing: data.analyzing || false,
        timeRemaining: data.timeRemaining || 0,
        nextAnalysisDue: data.nextAnalysisDue || null,
        manualUsed: data.manualUsedToday || 0,
        manualRemaining: data.manualRemaining || 10,
        timeUntilReset: data.timeUntilReset || 0,
        resetTime: data.resetTime || null
      };

      console.log('✅ Setting analysis status to:', newStatus);
      setAnalysisStatus(newStatus);
    } catch (err) {
      console.error('❌ Failed to load analysis status:', err);
    }
  };

  const loadDashboardData = async (forceSync = false) => {
    setLoading(true);
    setError(null);
    try {
      // Use Promise.allSettled to handle partial failures gracefully
      // Add cache-busting timestamp to prevent stale data
      const cacheBust = `?t=${Date.now()}`;
      const results = await Promise.allSettled([
        api.call(`/api/products${cacheBust}`),
        api.call(`/api/recommendations${cacheBust}`),
        api.call(`/api/orders${cacheBust}`),
        api.call(`/api/stats${cacheBust}`)
      ]);

      // Extract data with fallbacks for failed requests
      const productsData = results[0].status === 'fulfilled' ? results[0].value : { products: [] };
      const recsData = results[1].status === 'fulfilled' ? results[1].value : { recommendations: [] };
      const ordersData = results[2].status === 'fulfilled' ? results[2].value : { orders: [] };
      const statsData = results[3].status === 'fulfilled'
        ? results[3].value
        : {
            revenue: '0.00',
            totalRevenue: 0,
            orders: 0,
            totalOrders: 0,
            products: 0,
            productsAnalyzed: 0,
            averageOrderValue: '0.00',
            avgOrderValue: 0,
            profitIncrease: 0,
            totalAIProfit: 0
          };

      const loadedProducts = productsData.products || [];
      setProducts(loadedProducts);

      // CRITICAL FIX: Filter out rejected/applied and stale recommendations
      const allRecs = recsData.recommendations || [];
      console.log(`🔍 Frontend received ${allRecs.length} recommendations from API`);
      allRecs.forEach((rec, idx) => {
        console.log(`   [${idx}] ID: ${rec.id}, Product ID: ${rec.product_id}, Recommended: $${rec.recommended_price}, Status: ${rec.status}`);
      });

      const validRecs = allRecs.filter(rec => {
        // Filter out rejected or applied recommendations
        if (rec.status === 'rejected' || rec.status === 'accepted') {
          console.log(`   ❌ Filtered out rec ${rec.id} due to status: ${rec.status}`);
          return false;
        }

        const product = loadedProducts.find(p => p.id === rec.product_id);
        if (!product) {
          console.log(`   ❌ Filtered out rec ${rec.id} - no matching product for product_id: ${rec.product_id}`);
          return false;
        }
        const currentPrice = parseFloat(product.price);
        const recommendedPrice = parseFloat(rec.recommended_price);
        const priceChange = Math.abs(recommendedPrice - currentPrice);
        if (priceChange < 0.01) {
          console.log(`   ❌ Filtered out rec ${rec.id} - price change too small: ${priceChange}`);
          return false;
        }
        console.log(`   ✅ Keeping rec ${rec.id} - ${product.title}: $${currentPrice} → $${recommendedPrice} (change: $${priceChange})`);
        return true;
      });
      console.log(`✅ After filtering: ${validRecs.length} valid recommendations`);
      setRecommendations(validRecs);
      setOrders(ordersData.orders || []);
      setStats(statsData);

      // LOAD SELECTIONS FROM DATABASE
      // Products already have selected_for_analysis field from backend
      const selectedFromDB = loadedProducts.filter(p => p.selected_for_analysis === true);
      const productsWithCost = loadedProducts.filter(p => parseFloat(p.cost_price || 0) > 0);

      console.log(`📦 Loaded ${loadedProducts.length} products, ${productsWithCost.length} with cost prices, ${selectedFromDB.length} selected`);

      if (selectedFromDB.length > 0) {
        // User has existing selections in DB - respect them
        const selectedIds = selectedFromDB.map(p => p.id);
        setSelectedProductIds(selectedIds);
        console.log(`✅ Loaded ${selectedIds.length} selected products from database`);
      } else if (productsWithCost.length > 0 && productsWithCost.length <= 10) {
        // No selections in DB AND ≤10 products → Auto-select all and save to DB
        const autoSelectIds = productsWithCost.map(p => p.id);
        console.log(`🔄 Auto-selecting ${autoSelectIds.length} products (≤10 with cost prices)...`);

        try {
          const response = await fetch(`${API_URL}/api/products/select-batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify({ productIds: autoSelectIds })
          });

          if (response.ok) {
            setSelectedProductIds(autoSelectIds);
            console.log(`✅ Auto-selected ${autoSelectIds.length} products and saved to DB`);
          } else {
            console.error('Failed to auto-select products');
          }
        } catch (err) {
          console.error('Auto-select error:', err);
        }
      } else if (productsWithCost.length > 10) {
        // >10 products available - user must manually select
        console.log(`⚠️ ${productsWithCost.length} products available - manual selection required (max 10)`);
        setSelectedProductIds([]);
      }

      // Check for new recommendations since last visit
      const lastViewed = localStorage.getItem('lastViewedRecommendations');
      const newRecs = recsData.recommendations || [];
      if (lastViewed && newRecs.length > 0) {
        // Filter recommendations created after last viewed time
        const lastViewedTime = parseInt(lastViewed);
        const newOnes = newRecs.filter(rec => {
          const recTime = new Date(rec.created_at).getTime();
          return recTime > lastViewedTime;
        });

        if (newOnes.length > 0) {
          setNewRecommendationsCount(newOnes.length);
          setShowNewRecommendationsAlert(true);
        }
      }

      // CRITICAL: Auto-sync products to get fresh sales data
      // Sync every 5 minutes instead of once per session to prevent stale data
      const lastSyncTime = sessionStorage.getItem('lastSyncTime');
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const shouldSync = !lastSyncTime || (now - parseInt(lastSyncTime)) > fiveMinutes;

      if (shouldSync && shopifyConnected && !forceSync) {
        console.log('🔄 Auto-syncing products to fetch latest sales data...');
        try {
          sessionStorage.setItem('lastSyncTime', now.toString());
          await api.call('/api/products/sync', { method: 'POST' });
          console.log('✅ Products auto-synced successfully, reloading dashboard...');
          // Reload after sync to show fresh data
          setTimeout(() => loadDashboardData(true), 2000);
        } catch (syncErr) {
          console.error('❌ Auto-sync failed:', syncErr);
          // Continue loading with cached data
        }
      } else if ((productsData.products || []).length === 0 && shopifyConnected && !forceSync) {
        // Fallback: If no products at all, still try to sync
        console.log('📦 No products found, triggering sync...');
        try {
          await api.call('/api/products/sync', { method: 'POST' });
          console.log('✅ Products synced, reloading...');
          setTimeout(() => loadDashboardData(true), 2000);
        } catch (syncErr) {
          console.error('Sync failed:', syncErr);
        }
      }

      // Log any failures for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['/api/products', '/api/recommendations', '/api/orders', '/api/stats'];
          console.warn(`Failed to load ${endpoints[index]}:`, result.reason);
        }
      });
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh products data from Shopify (without full page reload)
  const refreshProducts = async () => {
    setRefreshing(true);
    setError(null);
    try {
      console.log('🔄 Refreshing products from Shopify...');
      const response = await api.call('/api/products/refresh', { method: 'POST' });
      if (response.products) {
        setProducts(response.products);
        setSuccessMessage('✅ Products refreshed from Shopify!');
        setTimeout(() => setSuccessMessage(null), 3000);
        console.log(`✅ Refreshed ${response.products.length} products`);
      }
      // Only reload stats (NOT recommendations - those only appear after running analysis)
      const statsData = await api.call('/api/stats');
      setStats(statsData);
      // NOTE: Recommendations are NOT reloaded on refresh - user must run analysis to see new recommendations
    } catch (err) {
      console.error('Refresh error:', err);
      setError('Failed to refresh products. Try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setRefreshing(false);
    }
  };

  const applyRecommendation = async (recId, productId, newPrice) => {
    try {
      await api.call(`/api/recommendations/${recId}/apply`, {
        method: 'POST',
        body: JSON.stringify({ productId, newPrice })
      });

      // Optimistic update - remove recommendation immediately
      setRecommendations(prev => prev.filter(r => r.id !== recId));

      // Update product price in local state
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, price: newPrice } : p
      ));

      setSuccessMessage('✅ Price updated on Shopify! Refreshing data...');

      // Auto-refresh data after 2 seconds to update stats
      setTimeout(async () => {
        try {
          await refreshProducts();
          setSuccessMessage('✅ Price updated and data refreshed!');
          setTimeout(() => setSuccessMessage(null), 2000);
        } catch (err) {
          console.warn('Auto-refresh failed:', err);
          setSuccessMessage(null);
        }
      }, 2000);

    } catch (err) {
      setError(err.message);
      // Reload on error to ensure consistency
      loadDashboardData();
    }
  };

  const rejectRecommendation = async (recId, productId, e) => {
    if (e) e.preventDefault();
    try {
      await api.call(`/api/recommendations/${recId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ productId })
      });
      loadDashboardData();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleProductSelection = async (productId) => {
    const isCurrentlySelected = selectedProductIds.includes(productId);

    // Check 10 product limit before selecting
    if (!isCurrentlySelected && selectedProductIds.length >= 10) {
      setError('⚠️ Maximum 10 products can be selected for analysis (Pro Plan limit)');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      // Save to database via API
      const response = await fetch(`${API_URL}/api/products/${productId}/toggle-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ selected: !isCurrentlySelected })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update selection');
      }

      // Update local state only after successful DB update
      setSelectedProductIds(prev => {
        if (isCurrentlySelected) {
          return prev.filter(id => id !== productId);
        } else {
          return [...prev, productId];
        }
      });

      console.log(`✅ Product ${productId} ${isCurrentlySelected ? 'deselected' : 'selected'}`);
    } catch (error) {
      console.error('Toggle selection error:', error);
      setError(`Failed to update selection: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const selectAllProducts = async () => {
    const productsWithCost = products.filter(p => p.cost_price > 0);
    // AIRTIGHT: Limit to 10 products max
    const idsToSelect = productsWithCost.slice(0, 10).map(p => p.id);

    try {
      // Save to database via batch API
      const response = await fetch(`${API_URL}/api/products/select-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ productIds: idsToSelect })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to select all products');
      }

      // Update local state after successful DB update
      setSelectedProductIds(idsToSelect);
      console.log(`✅ Selected all ${idsToSelect.length} products`);
    } catch (error) {
      console.error('Select all error:', error);
      setError(`Failed to select all: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const clearAllSelections = async () => {
    try {
      // Save to database via batch API (empty array = clear all)
      const response = await fetch(`${API_URL}/api/products/select-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ productIds: [] })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to clear selections');
      }

      // Update local state after successful DB update
      setSelectedProductIds([]);
      console.log(`✅ Cleared all selections`);
    } catch (error) {
      console.error('Clear all error:', error);
      setError(`Failed to clear selections: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const runAnalysis = async () => {
    // CRITICAL: Check auth token first
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      setError('⚠️ Session expired. Please log out and log back in.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    // Check if any products have cost price set (frontend validation)
    const productsWithCost = products.filter(p => p.cost_price > 0);
    if (productsWithCost.length === 0) {
      setError('⚠️ Please set cost price for at least one product before running analysis. Click "Set Cost Price" on a product.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    // Validation for >10 products case
    if (products.length > 10 && selectedProductIds.length === 0) {
      setError('⚠️ Please select at least one product to analyze. Check the boxes next to products you want to analyze.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      setAnalysisStatus({ ...analysisStatus, analyzing: true });
      console.log('🔄 Running analysis with token:', authToken.substring(0, 20) + '...');
      const result = await api.call('/api/analysis', {
        method: 'POST',
        body: JSON.stringify({
          productIds: products.length > 10 ? selectedProductIds : productsWithCost.map(p => p.id)
        })
      });

      // DEBUG: Log the full result including debug data
      console.log('📊 ANALYSIS RESULT:', result);
      if (result._debug) {
        console.log('🐛 DEBUG DATA:');
        result._debug.forEach(item => {
          console.log(`   ${item.title}:`);
          console.log(`      price: ${item.price}`);
          console.log(`      cost_price: ${item.cost_price}`);
          console.log(`      belowCost: ${item.belowCost}`);
        });
      }

      // Update analysis status with backend response
      setAnalysisStatus({
        ...analysisStatus,
        analyzing: false,
        manualUsed: result.manualUsed || 0,
        manualRemaining: result.manualRemaining || 10
      });

      // Show results modal
      setAnalysisResults(result);
      setShowAnalysisResults(true);

      // Reload data to show new recommendations
      loadDashboardData();
      // Reload analysis status to get updated counts
      loadAnalysisStatus();
    } catch (err) {
      const errorMessage = err.message || 'Failed to run analysis';
      setError(errorMessage);
      setAnalysisStatus({ ...analysisStatus, analyzing: false });
      setTimeout(() => setError(null), 8000);
    }
  };

  const calculateAIProfit = () => {
    if (!stats) return '0.00';
    // Show ACTUAL profit made THIS MONTH from applied AI recommendations
    return parseFloat(stats.historicalProfit || 0).toFixed(2);
  };

  // Normalized stat helpers to gracefully handle mixed API payloads
  const getTotalRevenue = () => {
    if (!stats) return 0;
    const value = stats.totalRevenue ?? stats.revenue ?? 0;
    return parseFloat(value);
  };

  const getTotalOrders = () => {
    if (!stats) return 0;
    return stats.totalOrders ?? stats.orders ?? 0;
  };

  const getAvgOrderValue = () => {
    if (!stats) return 0;
    const direct = stats.avgOrderValue ?? stats.averageOrderValue;
    if (direct !== undefined && direct !== null) {
      return parseFloat(direct);
    }
    const ordersCount = getTotalOrders();
    if (ordersCount === 0) return 0;
    return getTotalRevenue() / ordersCount;
  };

  const getProductsAnalyzed = () => {
    if (!stats) return 0;
    // Prefer explicit count, fall back to products length as a last resort
    return stats.productsAnalyzed ?? stats.products ?? products.length ?? 0;
  };

  const handleLogout = async () => {
    localStorage.removeItem('authToken');
    onLogout();
  };

  const dismissNewRecommendationsAlert = () => {
    setShowNewRecommendationsAlert(false);
    setNewRecommendationsCount(0);
    // Update last viewed time to now
    localStorage.setItem('lastViewedRecommendations', Date.now().toString());
  };

  return (
    <div className="min-h-screen bg-dark-950">
      <CostPriceModal
        isOpen={showCostPriceModal}
        onClose={() => {
          setShowCostPriceModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onSave={() => {
          loadDashboardData();
          setSuccessMessage('✅ Cost price updated successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        }}
      />

      {/* Analysis Results Bottom Notification */}
      {showAnalysisResults && analysisResults && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-50 max-w-md animate-slide-up">
          <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center space-x-3">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-white">AI Analysis Complete</h3>
              </div>
              <button
                onClick={() => setShowAnalysisResults(false)}
                className="p-1 hover:bg-slate-700 rounded transition"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="p-4">
              {analysisResults.recommendationsCreated > 0 ? (
                <div className="space-y-2">
                  <p className="text-green-400 font-semibold flex items-center space-x-2">
                    <Check className="w-4 h-4" />
                    <span>Found {analysisResults.recommendationsCreated} price optimization{analysisResults.recommendationsCreated > 1 ? 's' : ''}!</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    Scroll down to see recommendations below
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-green-400 font-semibold flex items-center space-x-2">
                    <Check className="w-4 h-4" />
                    <span>All Prices Optimized!</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    No changes recommended at this time
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Manual analyses: {analysisStatus.manualUsed || 0}/10 (resets after 24hrs)
              </p>
            </div>
          </div>
        </div>
      )}

      <SettingsModal
        isOpen={showSettings}
        onClose={() => {
          setShowSettings(false);
          checkShopifyConnection();
          loadDashboardData();
        }}
        onConnect={checkShopifyConnection}
        shopifyConnected={shopifyConnected}
      />

      {/* Header */}
      <div className="border-b border-white/5 glass-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 sm:h-16 gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-emerald-500/20 rounded-lg">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
              </div>
              <span className="text-lg sm:text-2xl font-bold text-white">AutoMerchant Pricing AI</span>
            </div>

            {activeTab === 'dashboard' && analysisStatus.nextAnalysisDue && (
              <div className="flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-800/50 border border-emerald-500/30 rounded-lg w-full sm:w-auto">
                <span className="text-xs sm:text-sm text-gray-400">Next Analysis:</span>
                <NextAnalysisTime
                  nextAnalysisDue={analysisStatus.nextAnalysisDue}
                  showIcon={true}
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {userEmail !== 'benjamincao98@gmail.com' && (
                <div className="text-left sm:text-right mr-2 hidden sm:block">
                  <p className="text-xs text-gray-400">Logged in as</p>
                  <p className="text-sm text-white font-medium truncate max-w-[150px]">{userEmail}</p>
                </div>
              )}
              {shopifyConnected ? (
                <div className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                  <Wifi className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400" />
                  <span className="text-xs sm:text-sm text-green-300 font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                  <WifiOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400" />
                  <span className="text-xs sm:text-sm text-yellow-300 font-medium">Disconnected</span>
                </div>
              )}
              <button onClick={() => setShowSettings(true)} className="flex items-center space-x-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-slate-700 rounded-lg text-white hover:bg-slate-600 transition text-sm">
                <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Settings</span>
              </button>
              <button onClick={handleLogout} className="flex items-center space-x-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-slate-700 rounded-lg text-white hover:bg-slate-600 transition text-sm">
                <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* New Recommendations Alert */}
        {showNewRecommendationsAlert && (
          <div className="mb-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">
                    {newRecommendationsCount} New Price Recommendation{newRecommendationsCount !== 1 ? 's' : ''} Available!
                  </h3>
                  <p className="text-purple-200 text-sm">
                    AI has analyzed your products while you were away. Check the recommendations below.
                  </p>
                </div>
              </div>
              <button
                onClick={dismissNewRecommendationsAlert}
                className="p-2 hover:bg-purple-500/20 rounded-lg transition"
              >
                <X className="w-5 h-5 text-purple-300" />
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'dashboard'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5" />
              <span>Dashboard</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'orders'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-5 h-5" />
              <span>Orders</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('roi')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'roi'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5" />
              <span>ROI Calculator</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('updates')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'updates'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <span className="text-xl">✨</span>
              <span>UPDATES</span>
            </div>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-400 font-medium">{successMessage}</p>
          </div>
        )}


        {/* Shopify Not Connected Warning */}
        {!shopifyConnected && activeTab === 'dashboard' && (
          <div className="mb-8 p-6 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-start space-x-4">
            <WifiOff className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-blue-300 font-semibold mb-1">Connecting to Shopify...</h3>
              <p className="text-blue-200/80 text-sm">Your admin has assigned you a Shopify app. Connecting automatically...</p>
              <div className="mt-3 flex items-center space-x-2">
                <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
                <span className="text-blue-300 text-sm">Redirecting to Shopify authorization...</span>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <>
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-600/20 to-blue-600/5 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Package className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-blue-300 text-sm font-medium mb-1">Total Products</p>
                  <p className="text-white text-3xl font-bold">{products.length}</p>
                </div>
                <div className="bg-gradient-to-br from-green-600/20 to-green-600/5 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                  <p className="text-green-300 text-sm font-medium mb-1">Revenue (30d)</p>
                  <p className="text-white text-3xl font-bold">${getTotalRevenue().toFixed(2)}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <ShoppingCart className="w-8 h-8 text-emerald-400" />
                  </div>
                  <p className="text-emerald-300 text-sm font-medium mb-1">Total Orders</p>
                  <p className="text-white text-3xl font-bold">{getTotalOrders()}</p>
                </div>
                <div className="bg-gradient-to-br from-pink-600/20 to-pink-600/5 border border-pink-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-8 h-8 text-pink-400" />
                  </div>
                  <p className="text-pink-300 text-sm font-medium mb-1">AI Profit Made This Month</p>
                  <p className="text-white text-3xl font-bold">${calculateAIProfit()}<span className="text-lg text-gray-400">/mo</span></p>
                  <p className="text-pink-200/60 text-xs mt-1">From applied AI price changes</p>
                </div>
              </div>
            )}

            {/* Analysis Status & Button */}
            <div className="mb-8 space-y-4">
              {/* Countdown Timer & Manual Resets Counter */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-gray-400 text-sm">Next Auto-Analysis</p>
                      <div className="text-white text-xl font-bold">
                        <NextAnalysisTime
                          nextAnalysisDue={analysisStatus.nextAnalysisDue}
                          showIcon={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    <div className="flex-1">
                      <p className="text-gray-400 text-sm">Manual Analyses Today</p>
                      <p className="text-white text-xl font-bold">
                        {analysisStatus.manualUsed || 0}/10
                        {(analysisStatus.manualRemaining || 10) === 0 && <span className="text-red-400 text-sm ml-2">(Limit Reached)</span>}
                      </p>
                      {analysisStatus.timeUntilReset > 0 && (
                        <div className="mt-2">
                          <ResetCountdownTimer
                            timeUntilReset={Number(analysisStatus.timeUntilReset)}
                            onRefresh={loadAnalysisStatus}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sync Products Button - ALWAYS VISIBLE */}
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      setLoading(true);
                      setError(null);
                      await api.call('/api/products/sync', { method: 'POST' });
                      await loadDashboardData();
                      // Success - data reloaded
                    } catch (err) {
                      setError('Failed to sync products: ' + (err.message || 'Unknown error'));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !shopifyConnected}
                  className="flex items-center justify-center space-x-3 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition shadow-lg shadow-green-500/20"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span>Sync Products from Shopify</span>
                </button>

                {/* Analysis Button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); runAnalysis(); }}
                  disabled={analysisStatus.analyzing || !shopifyConnected || (analysisStatus.manualRemaining || 10) === 0}
                  className="flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/20"
                >
                  {analysisStatus.analyzing ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Analyzing Products...</span>
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-5 h-5" />
                      <span>Run AI Analysis Now</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Product Selection UI - Shows only when >10 products */}
            {products.length > 10 && (
              <div className="mb-8 p-6 border border-emerald-500/30 bg-emerald-500/10 rounded-xl backdrop-blur-sm">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-white mb-2">Select Products to Analyze</h3>
                  <p className="text-gray-300">
                    You have {products.length} products. Select up to 10 to analyze. Only products with cost prices set can be selected.
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    ℹ️ We limit analysis to 10 products at a time to ensure fast, accurate recommendations.
                  </p>
                </div>

                {/* Search and Selection Controls */}
                <div className="mb-4 space-y-3">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="🔍 Search products..."
                      value={productSearchQuery}
                      onChange={(e) => setProductSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>

                  {/* Select All / Clear All Buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-3">
                      <button
                        onClick={selectAllProducts}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center space-x-2"
                      >
                        <CheckSquare className="w-4 h-4" />
                        <span>Select First 10 (with cost price)</span>
                      </button>
                      <button
                        onClick={clearAllSelections}
                        className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-semibold transition flex items-center space-x-2"
                      >
                        <Square className="w-4 h-4" />
                        <span>Clear All</span>
                      </button>
                    </div>
                    <div className="text-white font-semibold">
                      {selectedProductIds.length} / 10 selected
                    </div>
                  </div>
                </div>

                {/* Info Message */}
                {selectedProductIds.length > 0 && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-300 text-sm">
                      ✓ {selectedProductIds.length} product{selectedProductIds.length !== 1 ? 's' : ''} selected. Click "Run AI Analysis Now" above to analyze.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Auto-select message for ≤10 products */}
            {products.length > 0 && products.length <= 10 && (
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-300 font-medium">
                  ✅ All {products.length} products will be analyzed automatically (products with cost prices set)
                </p>
              </div>
            )}

            {/* Products List Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">Your Products</h3>
                <button
                  onClick={refreshProducts}
                  disabled={refreshing}
                  className="flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-white rounded-lg font-medium transition text-sm"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </button>
              </div>
              {products.length === 0 ? (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
                  <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-white mb-2">No Products Yet</h4>
                  <p className="text-gray-400 mb-4">Sync your products from Shopify to get started</p>
                  <button
                    onClick={async () => {
                      try {
                        await api.call('/api/products/sync', { method: 'POST' });
                        loadDashboardData();
                      } catch (err) {
                        setError('Failed to sync products');
                      }
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white rounded-lg font-semibold transition-all duration-300 shadow-md shadow-emerald-500/20"
                  >
                    <RefreshCw className="w-5 h-5 inline-block mr-2" />
                    Sync Products
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products
                    .filter(product =>
                      product.title.toLowerCase().includes(productSearchQuery.toLowerCase())
                    )
                    .map((product) => {
                      const isSelected = selectedProductIds.includes(product.id);
                      const canBeSelected = product.cost_price > 0;
                      const showCheckbox = products.length > 10;

                      return (
                    <div
                      key={product.id}
                      className={`bg-gradient-to-br from-slate-800/80 to-slate-800/40 border rounded-xl p-6 transition ${
                        isSelected ? 'border-emerald-500 shadow-lg shadow-emerald-500/20' : 'border-slate-700 hover:border-emerald-500/50'
                      }`}
                    >
                      {/* Checkbox for >10 products */}
                      {showCheckbox && (
                        <div className="mb-3">
                          <label className="flex items-center space-x-3 cursor-pointer group">
                            <div
                              onClick={() => canBeSelected && toggleProductSelection(product.id)}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition ${
                                canBeSelected
                                  ? isSelected
                                    ? 'bg-emerald-600 border-emerald-600'
                                    : 'border-slate-500 group-hover:border-emerald-500'
                                  : 'border-slate-700 bg-slate-800 cursor-not-allowed opacity-50'
                              }`}
                            >
                              {isSelected && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <span className={`text-sm font-medium ${canBeSelected ? 'text-white' : 'text-gray-500'}`}>
                              {isSelected ? 'Selected for analysis' : canBeSelected ? 'Select for analysis' : 'Set cost price first'}
                            </span>
                          </label>
                        </div>
                      )}

                      <div className="mb-4">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.title} className="w-full h-48 object-cover rounded-lg mb-4" />
                        ) : (
                          <div className="w-full h-48 bg-slate-700/50 rounded-lg mb-4 flex items-center justify-center">
                            <Package className="w-16 h-16 text-gray-600" />
                          </div>
                        )}
                        <h4 className="text-lg font-bold text-white mb-2">{product.title}</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Price:</span>
                            <span className="text-white font-semibold">${parseFloat(product.price).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Cost:</span>
                            <span className={`font-semibold ${product.cost_price > 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                              {product.cost_price > 0 ? `$${parseFloat(product.cost_price).toFixed(2)}` : 'Not set'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Inventory:</span>
                            <span className="text-white font-semibold">{product.inventory}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">Sales (30d):</span>
                            <span className="text-white font-semibold">{product.total_sales_30d || 0}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowCostPriceModal(true);
                        }}
                        className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition flex items-center justify-center space-x-2"
                      >
                        <DollarSign className="w-4 h-4" />
                        <span>Set Cost Price</span>
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recommendations Section */}
            {recommendations.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white">AI Recommendations</h3>
                  <button
                    onClick={() => setActiveTab('roi')}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:scale-105 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-purple-500/50"
                  >
                    <BarChart3 className="w-4 h-4" />
                    View ROI Calculator - See Total Estimated Profit
                  </button>
                </div>
                <div className="space-y-4">
                  {recommendations
                    .map((rec) => {
                    const isExpanded = expandedRecommendation === rec.id;
                    const product = products.find(p => p.id === rec.product_id);
                    if (!product) return null; // Skip if product not found

                    const currentPrice = parseFloat(product.price) || 0;
                    const recommendedPrice = parseFloat(rec.recommended_price) || currentPrice;
                    const priceChange = recommendedPrice - currentPrice;

                    // Determine if this is an informational recommendation (no price change recommended)
                    const isInformational = !rec.recommended_price || Math.abs(priceChange) < 0.01;

                    // CRITICAL FIX: Handle edge case where price change is 0 or negligible
                    const isNegligibleChange = Math.abs(priceChange) < 0.01;
                    const isIncrease = priceChange > 0.01; // Must be at least 1 cent increase
                    const isDecrease = priceChange < -0.01; // Must be at least 1 cent decrease
                    const changePercent = (Math.abs(priceChange) / currentPrice) * 100;

                    // Calculate estimated monthly profit increase (with elasticity)
                    const salesVelocity = parseFloat(product.sales_velocity) || 0;
                    const costPrice = parseFloat(product.cost_price) || 0;

                    // Use elasticity to estimate new velocity (default -1.2)
                    const elasticity = -1.2;
                    const priceRatio = recommendedPrice / currentPrice;
                    const velocityMultiplier = Math.pow(priceRatio, elasticity);
                    const newVelocity = salesVelocity * velocityMultiplier;

                    // Calculate actual profit change accounting for cost and elasticity
                    const currentMonthlyProfit = (currentPrice - costPrice) * salesVelocity * 30;
                    const newMonthlyProfit = (recommendedPrice - costPrice) * newVelocity * 30;
                    const monthlyProfitIncrease = costPrice > 0
                      ? (newMonthlyProfit - currentMonthlyProfit)
                      : (priceChange * salesVelocity * 30); // Fallback if no cost

                    return (
                    <div key={rec.id} className={`p-6 bg-gradient-to-br rounded-xl relative transition border-2 ${
                      rec.urgency === 'CRITICAL' ? 'from-red-900/30 to-red-900/10 border-red-500/50' :
                      rec.urgency === 'URGENT' ? 'from-orange-900/30 to-orange-900/10 border-orange-500/50' :
                      rec.urgency === 'HIGH' ? 'from-yellow-900/30 to-yellow-900/10 border-yellow-500/50' :
                      'from-purple-600/20 to-pink-600/20 border-purple-500/30'
                    }`}>
                      {/* Header with Product Image and Title */}
                      <div className="flex items-start gap-4 mb-4">
                        {product?.image_url && (
                          <img
                            src={product.image_url}
                            alt={rec.title}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 pr-12">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {/* Quick Win Badge - High profit + High confidence */}
                            {monthlyProfitIncrease > 50 && rec.confidence >= 60 && (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-500 to-orange-500 text-black animate-pulse">
                                ⚡ QUICK WIN
                              </span>
                            )}
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              rec.urgency === 'CRITICAL' ? 'bg-red-500 text-white' :
                              rec.urgency === 'URGENT' ? 'bg-orange-500 text-white' :
                              rec.urgency === 'HIGH' ? 'bg-yellow-500 text-black' :
                              'bg-blue-500 text-white'
                            }`}>
                              {rec.urgency === 'CRITICAL' ? '🚨' : rec.urgency === 'URGENT' ? '⚠️' : rec.urgency === 'HIGH' ? '📊' : '💡'} {rec.urgency}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-900/50 text-white">
                              {rec.confidence}% Confidence
                            </span>
                          </div>
                          <h4 className="text-xl font-bold text-white mb-2">{rec.title}</h4>
                        </div>
                        <button
                          onClick={(e) => rejectRecommendation(rec.id, rec.product_id, e)}
                          className="absolute top-4 right-4 p-2 hover:bg-slate-700/50 rounded-lg transition"
                          title="Dismiss recommendation"
                          type="button"
                        >
                          <X className="w-5 h-5 text-gray-400 hover:text-white" />
                        </button>
                      </div>

                      {/* Recommendation Summary */}
                      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-5 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-gray-400 text-sm font-semibold">Recommended Action:</p>
                          <p className={`text-2xl font-bold ${isNegligibleChange ? 'text-gray-400' : isIncrease ? 'text-green-400' : 'text-yellow-400'}`}>
                            {isNegligibleChange ? '✓ Price Optimal' : isIncrease ? '📈 Increase Price' : '📉 Lower Price'}
                          </p>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg mb-3">
                          <div>
                            <p className="text-gray-400 text-sm">New Price:</p>
                            <p className="text-3xl font-bold text-white">
                              ${parseFloat(rec.recommended_price).toFixed(2)}
                            </p>
                          </div>
                          <div className="text-center px-4">
                            <p className="text-gray-400 text-sm">Change:</p>
                            <p className={`text-2xl font-bold ${isNegligibleChange ? 'text-gray-400' : isIncrease ? 'text-green-400' : 'text-yellow-400'}`}>
                              {isNegligibleChange ? '$0.00' : `${isIncrease ? '+' : '-'}$${Math.abs(priceChange).toFixed(2)}`}
                            </p>
                            <p className="text-sm text-gray-400">({changePercent.toFixed(1)}%)</p>
                          </div>
                          <div className="text-right">
                            <p className="text-gray-400 text-sm">Current:</p>
                            <p className="text-2xl font-bold text-white">
                              ${currentPrice.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        {/* Profit Increase - Always show if there's sales velocity */}
                        {salesVelocity > 0 && (
                          <div className="p-3 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <TrendingUp className="w-5 h-5 text-green-400" />
                                <span className="text-gray-300 text-sm font-medium">Estimated Monthly Profit Increase:</span>
                              </div>
                              <span className={`text-2xl font-bold ${isNegligibleChange ? 'text-gray-400' : 'text-green-400'}`}>
                                {isNegligibleChange ? '$0.00/mo' : `${isIncrease ? '+' : ''}$${monthlyProfitIncrease.toFixed(2)}/mo`}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                              Based on {salesVelocity.toFixed(2)} sales/day × ${Math.abs(priceChange).toFixed(2)} price change × 30 days
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Reasoning */}
                      <div className="bg-purple-950/30 border border-purple-500/30 rounded-lg p-4 mb-4">
                        <p className="text-sm text-purple-300 font-semibold mb-2">💡 Why this recommendation:</p>
                        <p className="text-gray-200 leading-relaxed whitespace-pre-line">{rec.reasoning}</p>
                      </div>

                      {/* Expandable Details */}
                      <button
                        onClick={() => setExpandedRecommendation(isExpanded ? null : rec.id)}
                        className="w-full text-left px-4 py-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition flex items-center justify-between mb-4"
                      >
                        <span className="text-blue-300 font-semibold">
                          {isExpanded ? '📖 Hide' : '📖 Show'} Detailed Analysis
                        </span>
                        <span className="text-2xl">{isExpanded ? '▲' : '▼'}</span>
                      </button>

                      {isExpanded && product && (
                        <div className="mb-4 p-5 bg-slate-900/70 border border-slate-700 rounded-lg space-y-4">
                          {/* Inputs AI Used */}
                          <div>
                            <p className="text-sm font-semibold text-gray-300 mb-3">📊 Inputs AI Used:</p>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Current Price</p>
                                <p className="text-white font-bold">${currentPrice.toFixed(2)}</p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Cost Price</p>
                                <p className="text-white font-bold">${product.cost_price?.toFixed(2) || 'Not set'}</p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Current Margin</p>
                                <p className="text-white font-bold">
                                  {product.cost_price ? `${(((currentPrice - product.cost_price) / currentPrice) * 100).toFixed(1)}%` : 'N/A'}
                                </p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">New Margin</p>
                                <p className="text-green-400 font-bold">
                                  {product.cost_price ? `${(((parseFloat(rec.recommended_price) - product.cost_price) / parseFloat(rec.recommended_price)) * 100).toFixed(1)}%` : 'N/A'}
                                </p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Sales (30d)</p>
                                <p className="text-white font-bold">{product.total_sales_30d || 0} units</p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Sales Velocity</p>
                                <p className="text-white font-bold">
                                  {(product.sales_velocity || 0).toFixed(2)}/day
                                </p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Inventory</p>
                                <p className="text-white font-bold">{product.inventory || 0} units</p>
                              </div>
                              <div className="p-3 bg-slate-800 rounded">
                                <p className="text-gray-400">Revenue (30d)</p>
                                <p className="text-white font-bold">${(product.revenue_30d || 0).toFixed(2)}</p>
                              </div>
                            </div>
                          </div>

                          {/* Safety Checks */}
                          <div className="border-t border-slate-700 pt-4">
                            <p className="text-sm font-semibold text-gray-300 mb-3">🛡️ Safety Checks AI Performed:</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <span className="text-green-400">✓</span>
                                <span className="text-gray-300">
                                  {parseFloat(rec.recommended_price) >= (product.cost_price || 0)
                                    ? `Won't sell below cost (new price $${parseFloat(rec.recommended_price).toFixed(2)} > cost $${(product.cost_price || 0).toFixed(2)})`
                                    : `⚠️ WARNING: Below cost`}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-green-400">✓</span>
                                <span className="text-gray-300">
                                  {Math.abs(priceChange) <= currentPrice * 0.25
                                    ? `Change within safe limits (${changePercent.toFixed(1)}% ≤ 25% max)`
                                    : `Large change (${changePercent.toFixed(1)}%)`}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-green-400">✓</span>
                                <span className="text-gray-300">Based on actual sales data ({product.total_sales_30d || 0} units in 30 days)</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-green-400">✓</span>
                                <span className="text-gray-300">Data reliability verified before recommendation</span>
                              </div>
                            </div>
                          </div>

                          {/* Assumptions */}
                          <div className="border-t border-slate-700 pt-4">
                            <p className="text-sm font-semibold text-gray-300 mb-2">📋 Assumptions Made:</p>
                            <ul className="text-sm text-gray-400 space-y-1">
                              <li>• Sales velocity will remain similar after price change</li>
                              <li>• Customer demand has some price elasticity</li>
                              <li>• Your cost prices are accurate</li>
                              <li>• Market conditions remain stable</li>
                            </ul>
                          </div>

                          {/* Your Control */}
                          <div className="border-t border-slate-700 pt-4">
                            <p className="text-sm font-semibold text-gray-300 mb-2">⚙️ You Stay in Control:</p>
                            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                              <p className="text-sm text-blue-200">
                                <strong>This is a recommendation only.</strong> You decide whether to apply it.
                                You can also manually adjust the price to any value in Shopify.
                                <strong className="block mt-2">Nothing changes without your approval.</strong>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-3">
                        {isInformational ? (
                          // For informational recommendations (no price change), just show dismiss button
                          <button
                            onClick={(e) => rejectRecommendation(rec.id, rec.product_id, e)}
                            className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition flex items-center justify-center space-x-2"
                            type="button"
                          >
                            <Check className="w-5 h-5" />
                            <span>Got It - Thanks for the Guidance!</span>
                          </button>
                        ) : (
                          // For actionable recommendations, show reject + apply
                          <>
                            <button
                              onClick={(e) => rejectRecommendation(rec.id, rec.product_id, e)}
                              className="px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-600 transition flex items-center space-x-2"
                              type="button"
                            >
                              <X className="w-4 h-4" />
                              <span>Reject</span>
                            </button>
                            <button
                              onClick={(e) => { e.preventDefault(); applyRecommendation(rec.id, rec.product_id, parseFloat(rec.recommended_price)); }}
                              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 transition flex items-center justify-center space-x-2 shadow-lg shadow-green-500/20"
                              type="button"
                            >
                              <Check className="w-5 h-5" />
                              <span>Apply This Price to Shopify</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : products.some(p => p.last_analyzed_at) ? (
              <div className="space-y-6">
                {/* Main "All Good" Header */}
                <div className="p-8 bg-green-500/10 border-2 border-green-500/30 rounded-xl text-center">
                  <div className="flex flex-col items-center">
                    <div className="text-6xl mb-4">✅</div>
                    <h3 className="text-2xl font-bold text-white mb-2">All Prices Look Good!</h3>
                    <p className="text-gray-300 mb-4 max-w-md">
                      Our AI analyzed your products and found no pricing issues that need immediate attention.
                    </p>
                    <div className="max-w-md bg-slate-900/50 border border-green-500/20 rounded-lg p-5 text-left">
                      <p className="text-sm text-gray-400 mb-2">✅ What AI Verified:</p>
                      <ul className="text-sm text-gray-300 space-y-1">
                        <li>• All margins are healthy (30%+)</li>
                        <li>• No products selling below cost</li>
                        <li>• Sales velocity looks reasonable</li>
                        <li>• No obvious pricing errors detected</li>
                      </ul>
                    </div>
                    <p className="text-sm text-gray-500 mt-4">
                      We'll continue monitoring. Run analysis again after more sales data comes in.
                    </p>
                  </div>
                </div>

                {/* Detailed Explainability - Show What AI Analyzed */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-white">🔍 What AI Analyzed</h4>
                    <span className="text-xs text-gray-400 bg-slate-700/50 px-3 py-1 rounded-full">Transparency Report</span>
                  </div>

                  {products.filter(p => p.last_analyzed_at).map(product => {
                    const cost = parseFloat(product.cost_price || 0);
                    const currentStorePrice = parseFloat(product.price);
                    const actualAvgPrice = (product.total_sales_30d || 0) > 0
                      ? (parseFloat(product.revenue_30d || 0) / parseFloat(product.total_sales_30d))
                      : currentStorePrice;
                    const margin = cost > 0 && actualAvgPrice > 0
                      ? (((actualAvgPrice - cost) / actualAvgPrice) * 100).toFixed(1)
                      : 0;
                    const liveMargin = cost > 0 && currentStorePrice > 0
                      ? (((currentStorePrice - cost) / currentStorePrice) * 100).toFixed(1)
                      : margin;
                    const salesVelocity = (product.sales_velocity || 0).toFixed(2);

                    const isAboveCost = actualAvgPrice > parseFloat(product.cost_price || 0);
                    const isMarginHealthy = parseFloat(margin) >= 30;
                    const hasIssues = !isAboveCost || !isMarginHealthy;
                    const statusLabel = hasIssues ? 'Needs Review' : 'Optimal';
                    const statusColor = hasIssues ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300';
                    const reasoningBorderColor = hasIssues ? 'border-red-500/20' : 'border-green-500/20';
                    const reasoningTextColor = hasIssues ? 'text-red-200' : 'text-green-200';

                    return (
                      <div key={product.id} className="mb-4 p-4 bg-slate-900/30 border border-slate-700/50 rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h5 className="text-white font-semibold">{product.title}</h5>
                            <p className="text-xs text-gray-400">Analyzed {new Date(product.last_analyzed_at).toLocaleString()}</p>
                          </div>
                          <span className={`text-xs ${statusColor} px-2 py-1 rounded`}>{statusLabel}</span>
                        </div>

                        {/* Inputs Used */}
                        <div className="mb-3">
                          <p className="text-xs text-gray-400 font-semibold mb-2">📊 Inputs AI Used:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Actual Avg Price (30d):</span>
                              <span className={`ml-2 font-semibold ${!isAboveCost ? 'text-red-400' : 'text-white'}`}>${actualAvgPrice.toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Cost Price:</span>
                              <span className="text-white ml-2 font-semibold">${parseFloat(product.cost_price || 0).toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Margin:</span>
                              <span className={`ml-2 font-semibold ${!isMarginHealthy ? 'text-red-400' : 'text-white'}`}>{margin}%</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Current Shopify Price:</span>
                              <span className="text-white ml-2 font-semibold">${currentStorePrice.toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Sales Velocity:</span>
                              <span className="text-white ml-2 font-semibold">{salesVelocity} units/day</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Sales (30d):</span>
                              <span className="text-white ml-2 font-semibold">{product.total_sales_30d || 0} units</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Inventory:</span>
                              <span className="text-white ml-2 font-semibold">{product.inventory} units</span>
                            </div>
                          </div>
                        </div>

                        {/* AI Decision Reasoning */}
                        <div className={`mb-3 p-3 bg-slate-800/50 border ${reasoningBorderColor} rounded`}>
                          <p className="text-xs text-gray-400 font-semibold mb-1">💡 AI Reasoning:</p>
                          <p className={`text-xs ${reasoningTextColor} leading-relaxed`}>
                            {hasIssues ? (
                              <>
                                ⚠️ <strong>Issues detected:</strong>
                                {!isAboveCost && ` Selling below cost (avg price $${actualAvgPrice.toFixed(2)} < cost $${parseFloat(product.cost_price || 0).toFixed(2)}).`}
                                {!isMarginHealthy && ` Margin ${margin}% is below 30% target.`}
                                {' '}Run a new analysis to get updated recommendations. Data may be stale - click Refresh to update from Shopify.
                              </>
                            ) : (
                              <>
                                Based on actual average selling price ${actualAvgPrice.toFixed(2)} (current Shopify price ${currentStorePrice.toFixed(2)}),
                                margin is {margin}% (target = 30%). Sales velocity of {salesVelocity} units/day with {product.total_sales_30d || 0} total sales indicates
                                healthy demand. No safety issues detected.
                              </>
                            )}
                          </p>
                        </div>

                        {/* Safety Checks */}
                        <div>
                          <p className="text-xs text-gray-400 font-semibold mb-2">🛡️ Safety Checks:</p>
                          <div className="space-y-1 text-xs text-gray-300">
                            <div className="flex items-center space-x-2">
                              {actualAvgPrice > parseFloat(product.cost_price || 0) ? (
                                <span className="text-green-400">✓</span>
                              ) : (
                                <span className="text-red-400">✗</span>
                              )}
                              <span>Not selling below cost ({actualAvgPrice > parseFloat(product.cost_price || 0) ? 'PASS' : 'FAIL'})</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {parseFloat(margin) >= 30 ? (
                                <span className="text-green-400">✓</span>
                              ) : (
                                <span className="text-red-400">✗</span>
                              )}
                              <span>Margin above 30% minimum ({margin}% {parseFloat(margin) >= 30 ? '≥' : '<'} 30%)</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {(product.total_sales_30d || 0) > 0 ? (
                                <span className="text-green-400">✓</span>
                              ) : (
                                <span className="text-yellow-400">⚠</span>
                              )}
                              <span>Data reliability {(product.total_sales_30d || 0) > 0 ? 'sufficient' : 'limited'} ({product.total_sales_30d || 0} sales)</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {parseFloat(margin) > 0 && parseFloat(margin) < 200 ? (
                                <span className="text-green-400">✓</span>
                              ) : (
                                <span className="text-red-400">✗</span>
                              )}
                              <span>Markup {parseFloat(margin) > 0 && parseFloat(margin) < 200 ? 'reasonable' : 'needs review'}</span>
                            </div>
                          </div>
                        </div>

                        {/* What AI Considered - Dynamic based on product status */}
                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <p className="text-xs text-gray-400 mb-1">⚖️ Alternatives AI Considered:</p>
                          <ul className="text-xs text-gray-500 space-y-1">
                            {hasIssues ? (
                              <>
                                <li>• Increase price → <span className="text-yellow-300 font-semibold">Recommended - run analysis to get specific recommendation</span></li>
                                <li>• Decrease price → Not advisable with current margin issues</li>
                                <li>• Keep current → <span className="text-red-300">Not optimal - losing money or margin too low</span></li>
                              </>
                            ) : (
                              <>
                                <li>• Increase price → Could improve margin but may reduce sales volume</li>
                                <li>• Decrease price → Not needed, current performance is healthy</li>
                                <li>• Keep current → <span className="text-green-300 font-semibold">Optimal choice - margins healthy</span></li>
                              </>
                            )}
                          </ul>
                        </div>
                      </div>
                    );
                  })}

                  <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-xs text-blue-200">
                      <strong>💡 Nothing changes without you:</strong> Even when AI finds opportunities,
                      all price changes require your manual approval. You stay in complete control.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 bg-blue-500/10 border-2 border-blue-500/30 rounded-xl text-center">
                <div className="text-6xl mb-4">💡</div>
                <h3 className="text-2xl font-bold text-white mb-2">Ready to Get Started?</h3>
                <p className="text-gray-300 mb-4 max-w-md mx-auto">
                  Set cost prices for your products and run your first AI analysis to get pricing recommendations.
                </p>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); runAnalysis(); }}
                  disabled={products.filter(p => p.cost_price > 0).length === 0}
                  className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-all duration-300 shadow-md shadow-emerald-500/20"
                >
                  Run First Analysis
                </button>
                <p className="text-sm text-gray-500 mt-3">
                  {products.filter(p => p.cost_price > 0).length === 0 && '⚠️ Set cost prices first'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Recent Orders</h2>
              <p className="text-gray-400">Last 30 days from your Shopify store</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 shadow-2xl">
              {!shopifyConnected ? (
                <div className="text-center py-16">
                  <div className="inline-block p-6 bg-yellow-500/10 rounded-2xl mb-4">
                    <WifiOff className="w-16 h-16 text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Shopify Not Connected</h3>
                  <p className="text-gray-400 mb-6">Connect your store to view order history</p>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white rounded-lg font-semibold transition-all duration-300 shadow-md shadow-emerald-500/20"
                  >
                    Connect Shopify
                  </button>
                </div>
              ) : loading ? (
                <div className="text-center py-16">
                  <RefreshCw className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Orders Yet</h3>
                  <p className="text-gray-400">Orders from the last 30 days will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Order #</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Customer</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Products</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Total</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Date</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} className="border-b border-slate-800 hover:bg-slate-700/20 transition">
                          <td className="py-3 px-4 text-white font-medium">#{order.order_number}</td>
                          <td className="py-3 px-4 text-gray-300">{order.customer_name || 'Guest'}</td>
                          <td className="py-3 px-4 text-gray-300 max-w-xs">
                            <div className="truncate" title={order.product_summary || `${order.line_items_count} items`}>
                              {order.product_summary || `${order.line_items_count} items`}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-white font-bold">${parseFloat(order.total_price).toFixed(2)}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              order.financial_status === 'paid' ? 'bg-green-500/20 text-green-300' :
                              order.financial_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                              'bg-gray-500/20 text-gray-300'
                            }`}>
                              {order.financial_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ROI Calculator Tab */}
        {activeTab === 'roi' && (
          <>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Impact Calculator</h2>
              <p className="text-gray-400">Honest metrics about what AI recommendations could change — based on YOUR actual data</p>
            </div>

            {!shopifyConnected ? (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
                <div className="inline-block p-6 bg-yellow-500/10 rounded-2xl mb-4">
                  <WifiOff className="w-16 h-16 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Shopify Not Connected</h3>
                <p className="text-gray-400 mb-6">Connect your store to see real calculations based on your actual sales data</p>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition"
                >
                  Connect Shopify
                </button>
              </div>
            ) : (
              <>
                {/* Estimated Monthly Value - AT THE TOP */}
                {stats && getTotalRevenue() > 0 && (
                  <div className="mb-6 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          Estimated Monthly Value
                        </h3>
                        <p className="text-white/90 text-sm">
                          Revenue increase from AI-optimized pricing
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white text-5xl font-bold">
                          ${stats?.historicalProfit ? parseFloat(stats.historicalProfit).toFixed(2) : '0.00'}
                        </p>
                        <p className="text-white/60 text-sm mt-1">per month</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Data Coverage Indicator */}
                <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-white">Data Coverage</h4>
                    <span className="text-xs text-gray-400">Last 30 days</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-white">{getTotalOrders()}</p>
                      <p className="text-xs text-gray-400">Orders analyzed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{products.length}</p>
                      <p className="text-xs text-gray-400">Products tracked</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{recommendations.length}</p>
                      <p className="text-xs text-gray-400">Active recommendations</p>
                    </div>
                  </div>
                </div>

                {/* MEASURED RESULTS (What's Real) */}
                <div className="mb-8 p-6 bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-2 border-green-500/50 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">✅ MEASURED: What AI Has Found</h3>
                    <span className="text-xs text-green-200/70 bg-green-500/20 px-3 py-1 rounded-full">Real Data</span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-green-200/80 text-sm mb-2">Potential Additional Profit (From Your Pending Recommendations)</p>
                      <p className="text-white text-4xl font-bold">
                        +${stats ? parseFloat(stats.profitIncrease || 0).toFixed(2) : '0.00'}
                      </p>
                      {recommendations.length > 0 ? (
                        <>
                          <p className="text-green-200/60 text-xs mt-2">
                            📊 Calculated from {recommendations.length} pending AI recommendation{recommendations.length !== 1 ? 's' : ''} based on your actual sales volume
                          </p>
                          <p className="text-green-200/60 text-xs mt-1">
                            ⚠️ This assumes sales velocity stays constant after price changes (which may or may not happen)
                          </p>
                        </>
                      ) : (
                        <p className="text-green-200/60 text-xs mt-2">
                          💡 You have no pending recommendations. Run an analysis or wait for the next auto-analysis to see potential profit.
                        </p>
                      )}
                    </div>
                    <div className="h-px bg-green-500/30"></div>
                    <div>
                      <p className="text-green-200/80 text-sm mb-2">Current 30-Day Revenue</p>
                      <p className="text-white text-2xl font-bold">
                        ${stats ? getTotalRevenue().toFixed(2) : '0.00'}
                      </p>
                      <p className="text-green-200/60 text-xs mt-1">From {getTotalOrders()} orders in the last 30 days</p>
                    </div>
                  </div>
                </div>

                {/* PROJECTED SCENARIOS (What Could Happen) */}
                <div className="mb-8 p-6 bg-slate-800/50 border border-slate-700 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">📈 PROJECTED: Three Scenarios</h3>
                    <span className="text-xs text-gray-400 bg-slate-700/50 px-3 py-1 rounded-full">Estimates Only</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    These are <span className="text-yellow-300">educated guesses</span> about what could happen if you apply recommendations.
                    Real results depend on customer behavior, competition, and market conditions.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Conservative Scenario */}
                    <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                      <p className="text-blue-300 text-sm font-semibold mb-3">Conservative</p>
                      <p className="text-white text-2xl font-bold mb-2">
                        +${stats ? (parseFloat(stats.profitIncrease || 0) * 0.5).toFixed(2) : '0.00'}/mo
                      </p>
                      <p className="text-xs text-gray-400 mb-3">50% of AI estimate</p>
                      <div className="text-xs text-gray-400 space-y-1">
                        <p>✓ Some customers resist price changes</p>
                        <p>✓ Sales velocity drops slightly</p>
                      </div>
                    </div>

                    {/* Moderate Scenario */}
                    <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                      <p className="text-purple-300 text-sm font-semibold mb-3">Moderate</p>
                      <p className="text-white text-2xl font-bold mb-2">
                        +${stats ? (parseFloat(stats.profitIncrease || 0) * 0.75).toFixed(2) : '0.00'}/mo
                      </p>
                      <p className="text-xs text-gray-400 mb-3">75% of AI estimate</p>
                      <div className="text-xs text-gray-400 space-y-1">
                        <p>✓ Minor impact on demand</p>
                        <p>✓ Most customers accept changes</p>
                      </div>
                    </div>

                    {/* Optimistic Scenario */}
                    <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <p className="text-green-300 text-sm font-semibold mb-3">Optimistic</p>
                      <p className="text-white text-2xl font-bold mb-2">
                        +${stats ? parseFloat(stats.profitIncrease || 0).toFixed(2) : '0.00'}/mo
                      </p>
                      <p className="text-xs text-gray-400 mb-3">100% of AI estimate</p>
                      <div className="text-xs text-gray-400 space-y-1">
                        <p>✓ Sales volume stays constant</p>
                        <p>✓ No demand sensitivity</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-200 text-xs">
                      <strong>Reality check:</strong> These scenarios assume sales volume doesn't change when prices do.
                      In reality, raising prices usually decreases sales slightly (price elasticity). We show multiple scenarios because we're honest about uncertainty.
                    </p>
                  </div>
                </div>

                {/* Assumptions Section */}
                <div className="mb-8 p-6 bg-slate-800/50 border border-slate-700 rounded-xl">
                  <h3 className="text-xl font-bold text-white mb-4">🔍 How We Calculate This</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start space-x-3">
                      <span className="text-purple-400 font-bold">1.</span>
                      <div>
                        <p className="text-white font-semibold">We look at your actual sales data</p>
                        <p className="text-gray-400">Orders from the last 30 days, product costs, current prices</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-purple-400 font-bold">2.</span>
                      <div>
                        <p className="text-white font-semibold">AI calculates profit per sale (current vs recommended price)</p>
                        <p className="text-gray-400">Example: $50 → $55 = +$5 profit per sale</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-purple-400 font-bold">3.</span>
                      <div>
                        <p className="text-white font-semibold">Multiply by your sales volume</p>
                        <p className="text-gray-400">If you sold 10 units: +$5 × 10 = +$50 additional profit</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-purple-400 font-bold">4.</span>
                      <div>
                        <p className="text-white font-semibold">Big assumption: Sales stay the same</p>
                        <p className="text-gray-400 text-yellow-200">⚠️ This may not be true! Higher prices can reduce demand.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {shopifyConnected && (
              <>
                {/* ROI Breakdown */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
                  <h3 className="text-xl font-bold text-white mb-6">How We Calculate ROI</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-white font-semibold mb-2">📊 Cost-Based Analysis</p>
                      <p className="text-gray-400 text-sm">
                        AI analyzes your cost prices and current sales to suggest optimal profit margins
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-2">💹 Margin Optimization</p>
                      <p className="text-gray-400 text-sm">
                        Get recommendations to improve margins while maintaining competitive pricing
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-2">⚡ On-Demand Analysis</p>
                      <p className="text-gray-400 text-sm">
                        Run AI analysis whenever you need fresh pricing recommendations for your products
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Detailed Statistics - ONLY IF CONNECTED */}
            {shopifyConnected && stats && (
              <div className="mt-8 bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-6">Detailed Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Total Revenue (30d)</p>
                    <p className="text-white text-3xl font-bold">${getTotalRevenue().toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Total Orders</p>
                    <p className="text-white text-3xl font-bold">{getTotalOrders()}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Average Order Value</p>
                    <p className="text-white text-3xl font-bold">${getAvgOrderValue().toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Products Analyzed</p>
                    <p className="text-white text-3xl font-bold">{getProductsAnalyzed()}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* UPDATES Tab */}
        {activeTab === 'updates' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-4xl">✨</span>
                <h2 className="text-3xl font-bold text-white">UPDATES - Jan 1, 2026</h2>
              </div>

              <ul className="space-y-3 text-gray-300 mb-8">
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 mt-1">•</span>
                  <div>
                    <strong className="text-white">Fixed critical bug</strong> causing negative price recommendations (4000% margin error)
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 mt-1">•</span>
                  <div>
                    <strong className="text-white">Enhanced low-data mode:</strong> Now estimates cost price (60% of current) when missing, still gives best recommendations
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 mt-1">•</span>
                  <div>
                    <strong className="text-white">Improved confidence scoring:</strong> Clear distinction between estimated vs actual cost data (25-35% vs 70-95%)
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 mt-1">•</span>
                  <div>
                    <strong className="text-white">Smarter margin targeting:</strong> All algorithms now use correct decimal format (0.40 = 40%)
                  </div>
                </li>
              </ul>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="glass-card-linear border-blue-500/30 rounded-lg p-6 border-glow-hover">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-3xl">🎯</span>
                    <h3 className="text-xl font-bold text-white">Smart Confidence</h3>
                  </div>
                  <p className="text-gray-300">
                    All products get recommendations. Low data = margin-based (35% confidence). High data = full ML (70-95% confidence).
                  </p>
                </div>

                <div className="glass-card-linear border-green-500/30 rounded-lg p-6 border-glow-hover">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-3xl">💰</span>
                    <h3 className="text-xl font-bold text-white">Profit-First</h3>
                  </div>
                  <p className="text-gray-300">
                    Every recommendation shows exact monthly profit impact. Clear reasoning. No guesswork.
                  </p>
                </div>

                <div className="glass-card-linear border-purple-500/30 rounded-lg p-6 border-glow-hover">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-3xl">🛡️</span>
                    <h3 className="text-xl font-bold text-white">Risk Protection</h3>
                  </div>
                  <p className="text-gray-300">
                    CVaR downside protection. Regret minimization. Never recommends below cost. Your margins are safe.
                  </p>
                </div>
              </div>

              <div className="bg-green-900/20 border-2 border-green-500/50 rounded-lg p-5">
                <p className="text-green-200 text-center">
                  <strong className="text-green-300">Graduate-level ML</strong> working for you 24/7. Bayesian learning, elasticity optimization, multi-objective Pareto frontiers.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductDashboard;
